package com.milta.feature.route.domain.entity

/**
 * Manager-facing team dashboard (MIL-38 — `GET /api/v1/routes`, Enterprise role only, defaults to
 * today). Replaces the former flat `List<Route>` shape returned by that same endpoint path: the
 * response is now a wrapper carrying day-level totals plus one summary per driver route.
 */
data class TeamRoutesOverview(
    /** Resolved date of this overview (yyyy-MM-dd) — echoes the request's `date` query param, or
     * today's date when omitted. */
    val date: String,
    val totalRoutes: Int,
    val totalDeliveries: Int,
    val completedDeliveries: Int,
    val routes: List<DriverRouteOverview>,
)

/** One driver's route as shown on the Manager's team dashboard for the requested day. */
data class DriverRouteOverview(
    val routeId: String,
    val driverId: String,
    val driverFirstName: String,
    val driverLastName: String,
    val status: RouteStatus,
    val startedAt: String?,
    val completedAt: String?,
    val completedStopsCount: Int,
    val totalStopsCount: Int,
    /** Somme des colis de la tournée (un arrêt peut en regrouper plusieurs). 0 = inconnu/ancien backend. */
    val totalPackagesCount: Int = 0,
    /** Identifiant de la répartition entre plusieurs chauffeurs ; null = tournée d'un seul chauffeur. */
    val batchId: String? = null,
    val failedStopsCount: Int = 0,
    val untreatedStopsCount: Int = 0,
) {
    /** Arrêts encore à traiter (ni livrés, ni en échec, ni non traités). */
    val pendingStopsCount: Int
        get() = (totalStopsCount - completedStopsCount - failedStopsCount - untreatedStopsCount).coerceAtLeast(0)

    /** L'arrêt courant du livreur (0 ou 1) : le premier arrêt à faire, seulement si la tournée est en cours. */
    val inProgressStopsCount: Int get() = if (status == RouteStatus.InProgress && pendingStopsCount > 0) 1 else 0

    /** Arrêts restant à faire, hors arrêt courant. */
    val todoStopsCount: Int get() = pendingStopsCount - inProgressStopsCount

    /** Le chauffeur a quitté le dépôt : tournée en cours ou déjà terminée (ou horodatée au démarrage). */
    val hasStarted: Boolean get() = status != RouteStatus.Scheduled || startedAt != null
    val progress: Float get() = if (totalStopsCount == 0) 0f else completedStopsCount.toFloat() / totalStopsCount
}
